import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import config from '../config';
import unifiedApi from '../services/unifiedApiClient';
import { getWebOrigin, isNativePlatform } from '../utils/platform';
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { setUserContext, addBreadcrumb } from '../sentry';
import { captureEvent, identifyUser, resetPostHog } from '../posthog';
import { clearWebAuthStorage } from '../lib/clearWebAuthStorage';

interface User {
  id: string;
  email: string;
  name: string;
  // KAN-name-capture: split-name parts captured by the post-magic-link
  // NamePromptGate. `first_name` is required by the gate; `last_name` is
  // optional; `display_name` defaults to first_name when blank. All three
  // are nullable on the DB row — legacy users predate the capture step and
  // are backfilled lazily (Stripe webhook on first checkout, admin edit,
  // or Profile · Settings).
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  // KAN-328: user-chosen handle. Canonical identity for any cross-user
  // surface (leaderboards, share-cards, future public profiles). `email`
  // and `name` stay self-only / admin-only; only `username` flows out.
  username: string;
  // KAN-328: false for legacy rows whose handle was auto-derived from
  // email local-part. UsernamePromptGate shows the confirm/change modal
  // until the user actively sets/confirms it.
  username_set_explicitly: boolean;
  subscription_tier: 'free' | 'degenerate';
  tier_name: string;
  is_active: boolean;
  // KAN-23643: soft-delete timestamp from users.deleted_at (KAN-21421).
  // Canonical "Inactive" signal for end-user surfaces — a NULL value means
  // the account is active, regardless of the legacy `is_active` flag which
  // can be set false for reasons that aren't user-meaningful (e.g. backend
  // request gating). The Security card on Profile reads this, not is_active.
  deleted_at: string | null;
  is_admin: boolean;
  email_verified: boolean;
  subscription_start_date?: string;
  subscription_end_date?: string;
  subscription_auto_renew: boolean;
  predictions_used_this_month: number;
  has_quick_pick: boolean;
  has_full_analysis: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string;
  auth_user_id: string;
  has_used_trial?: boolean;
  trial_started_at?: string;
  trial_ends_at?: string;
  // KAN-3056 Phase 2: onboarding tour lifecycle timestamps. NULL means
  // the state has not been reached. KAN-3057 (Phase 3) consumes these
  // in OnboardingTourGate to decide whether to auto-fire the tour.
  tour_started_at?: string | null;
  tour_completed_at?: string | null;
  tour_skipped_at?: string | null;
  // KAN-26249: IANA tz name (e.g. "America/New_York") chosen via Profile ->
  // Settings -> Time zone (KAN-12162). NULL = "auto" — fall through to the
  // device-resolved zone in useDisplayTimezone().
  preferred_timezone?: string | null;
}

// KAN-328: server-side validation reasons returned by the username RPCs.
// `available` for the live availability hook; `ok`/`reason` for set_username.
export type UsernameRejectReason =
  | 'empty'
  | 'too_short'
  | 'too_long'
  | 'bad_chars'
  | 'reserved'
  | 'taken'
  | 'not_authenticated';

export interface UsernameAvailability {
  available: boolean;
  reason?: UsernameRejectReason;
}

export interface SetUsernameResult {
  ok: boolean;
  username?: string;
  reason?: UsernameRejectReason;
}

interface AuthContextType {
  user: User | null;
  /** Supabase session — present once a token is rehydrated from localStorage.
   *  Available BEFORE the profile fetch resolves; used by ProtectedRoute to
   *  hold the page during rehydration without bouncing to /login. */
  hasSession: boolean;
  /** KAN-5912: true once `fetchUserProfile` has resolved at least once for the
   *  current session (real profile OR fallback retention after retries). The
   *  fallback path sets `is_admin=false`, so the admin route guard MUST wait
   *  for this flag before redirecting non-admins — otherwise a freshly
   *  rehydrated admin session bounces to `/` on every direct URL hit. Reset
   *  to false on logout. */
  profileLoaded: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    name: string,
    // KAN-328: signup form passes an explicit username — sent through
    // raw_user_meta_data so handle_new_user can stamp it onto public.users
    // with username_set_explicitly=true.
    username: string,
    clickwrap: {
      tos_accepted_at: string;
      tos_version: string;
      // KAN-152: marketing consent. Optional opt-in checkbox at signup.
      // null when the user didn't tick the box (NOT false — preserves the
      // distinction between "declined" and "never asked"). The timestamp
      // is set only when the user opts IN.
      marketing_consent?: boolean;
      marketing_consent_at?: string | null;
    }
  ) => Promise<{ requiresEmailConfirm: boolean }>;
  logout: () => Promise<void>;
  loading: boolean;
  isInitializing: boolean;
  session: Session | null;
  updateProfile: (data: { name?: string; email?: string; current_password?: string; new_password?: string }) => Promise<void>;
  // KAN-328: live availability check. Anon-callable RPC — used by the
  // signup form before the user has a session.
  checkUsernameAvailability: (username: string) => Promise<UsernameAvailability>;
  // KAN-328: authenticated update. Used by UsernamePromptModal + Profile
  // edit flow. Sets username_set_explicitly=true on success and refreshes
  // the local user object so Profile + Track pivot to the new handle.
  setUsername: (username: string) => Promise<SetUsernameResult>;
  // KAN-name-capture: post-magic-link first/last/display capture. Mirrors
  // the setUsername raw-fetch + access-token pattern. Returns
  //   { ok: true } on success
  //   { ok: false, reason: 'first_name_required'|'too_long'|'not_authenticated'|'user_row_not_found' }
  // on failure. On success the local user profile is refreshed so
  // first_name/last_name/display_name flow into every consumer.
  setNameDetails: (
    args: { first_name: string; last_name?: string; display_name?: string }
  ) => Promise<{ ok: boolean; reason?: string }>;
  upgradeSubscription: (tier: string, promoCode?: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  // Legacy compatibility (no-op)
  getToken: () => string | null;
  setToken: (token: string) => void;
  clearToken: () => void;
  isTokenExpired: () => boolean;
  clearCorruptedAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within SupabaseAuthProvider');
  }
  return context;
};

export const SupabaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  // KAN-5912: track whether the profile fetch (real or fallback retention)
  // has completed for the current session. Distinguishes "fallback user with
  // is_admin=false because we don't know yet" from "real profile loaded with
  // is_admin=false". ProtectedRoute uses this to hold admin routes until
  // is_admin is definitively known.
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    console.log('[SupabaseAuth] Provider mounted');
  }, []);

  // Sync user identity into Sentry + PostHog whenever the auth user changes.
  // Distinct ID = Supabase auth user id (`auth_user_id`) — same value used
  // by the FastAPI backend middleware and the edge-function wrapper, so all
  // three surfaces share a join key in PostHog. PII-free — we send only
  // tier + admin flag + signup timestamps as user properties.
  //
  // KAN-374: identify() runs on every signin (login + signup confirm). The
  // critical guarantee is that the anonymous distinct_id PostHog assigned
  // before login gets aliased to the auth user id, so server-side prediction
  // events keyed by JWT `sub` join with frontend `$pageview` events keyed
  // by the previously-anonymous distinct_id. Without this call we'd see
  // two PostHog person rows per user (one anon, one authed).
  useEffect(() => {
    if (user?.auth_user_id) {
      // KAN-10895 (2026-05-12): include email so Sentry crash reports
      // resolve to a real user without a separate Supabase lookup. This
      // flips the App Privacy nutrition label Diagnostics rows to "Linked
      // to identity" — locked decision on KAN-10863.
      setUserContext({ id: user.auth_user_id, email: user.email });
      identifyUser(user.auth_user_id, {
        tier: user.subscription_tier,
        tier_name: user.tier_name,
        is_admin: user.is_admin,
        // KAN-374: add the cohort-defining traits the ticket called for.
        // `signup_at` from public.users.created_at — useful for retention
        // funnels. `email_verified` from auth.users.email_confirmed_at —
        // splits unconfirmed-signup vs confirmed-active cohorts.
        signup_at: user.created_at,
        email_verified: user.email_verified,
      });
    } else {
      setUserContext(null);
      resetPostHog();
    }
  }, [
    user?.auth_user_id,
    user?.subscription_tier,
    user?.tier_name,
    user?.is_admin,
    user?.created_at,
    user?.email_verified,
  ]);

  // KAN-46: Cached tier — written on every successful profile fetch, read on
  // fallback so a transient profile-fetch failure doesn't silently downgrade a
  // paying user to FREE-tier UX. Cleared on logout.
  const TIER_CACHE_KEY = 'aightbet:last-known-tier';
  const TIER_NAME_CACHE_KEY = 'aightbet:last-known-tier-name';

  // Create fallback user from auth data when profile fetch fails
  const createFallbackUser = (authUser: SupabaseUser): User => {
    console.log('[SupabaseAuth] Creating fallback user from auth data');

    const now = new Date().toISOString();

    // Restore the user's most recent known tier from localStorage if available.
    // KAN-46: prevents silent FREE-tier downgrade when the profile endpoint is
    // briefly unavailable (e.g. during PostgREST schema cache reload).
    let cachedTier: 'free' | 'degenerate' = 'free';
    let cachedTierName = 'FREE';
    try {
      const t = localStorage.getItem(TIER_CACHE_KEY);
      const tn = localStorage.getItem(TIER_NAME_CACHE_KEY);
      if (t === 'free' || t === 'degenerate') cachedTier = t;
      if (tn) cachedTierName = tn;
    } catch { /* SSR / restricted storage — fall back to free */ }

    return {
      id: authUser.id, // Use auth ID as fallback
      email: authUser.email || '',
      name: authUser.user_metadata?.name || '',
      // KAN-328: optimistic fallback before the real profile fetch lands.
      // Auth user_metadata may have a username if signup just completed;
      // otherwise empty string — UsernamePromptGate keys off
      // `username_set_explicitly === false` regardless of value, so the
      // empty string here doesn't break the prompt.
      username: authUser.user_metadata?.username || '',
      username_set_explicitly: false,
      subscription_tier: cachedTier,
      tier_name: cachedTierName,
      is_active: true,
      deleted_at: null,
      is_admin: false,
      email_verified: !!authUser.email_confirmed_at,
      subscription_start_date: undefined,
      subscription_end_date: undefined,
      subscription_auto_renew: true,
      predictions_used_this_month: 0,
      has_quick_pick: true,
      has_full_analysis: false,
      created_at: authUser.created_at || now,
      updated_at: now,
      last_login: undefined,
      auth_user_id: authUser.id,
      preferred_timezone: null,
    };
  };

  // Fetch user profile from public.users table with timeout and retry
  const fetchUserProfile = async (
    authUser: SupabaseUser,
    providedSession?: Session | null,
    retryCount = 0
  ): Promise<User | null> => {
    const startTime = Date.now();

    try {
      console.log(`[SupabaseAuth] Fetching profile for auth user: ${authUser.id} (attempt ${retryCount + 1})`);

      // DEADLOCK FIX: Use provided session from auth callback instead of calling getSession()
      // Calling getSession() from within onAuthStateChange creates a deadlock
      if (providedSession) {
        console.log('[SupabaseAuth] Using provided session from auth callback, access_token present:', !!providedSession.access_token);
      } else {
        console.log('[SupabaseAuth] No session provided, will rely on Supabase client auto-auth');
        // Don't call getSession() here - it can deadlock if called from auth callback
        // The Supabase client will automatically attach the auth token from storage
      }

      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Profile fetch timeout')), 10000);
      });

      // Create fetch promise
      const fetchPromise = supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single();

      // Race between fetch and timeout
      const { data: profile, error } = await Promise.race([
        fetchPromise,
        timeoutPromise,
      ]).catch((err) => {
        console.error('[SupabaseAuth] Fetch or timeout error:', err);
        return { data: null, error: err };
      });

      const elapsed = Date.now() - startTime;
      console.log(`[SupabaseAuth] Profile fetch completed in ${elapsed}ms`);
      console.log('[SupabaseAuth] Promise.race result:', {
        hasData: !!profile,
        dataValue: profile,
        hasError: !!error,
        errorCode: error?.code,
        errorMessage: error?.message
      });

      if (error) {
        console.error('[SupabaseAuth] Error fetching profile:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });

        // Retry once on first failure
        if (retryCount === 0) {
          console.log('[SupabaseAuth] Retrying profile fetch in 2 seconds...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          return fetchUserProfile(authUser, providedSession, retryCount + 1);
        }

        // After retry fails, use fallback
        console.warn('[SupabaseAuth] Profile fetch failed after retry, using fallback');
        return createFallbackUser(authUser);
      }

      if (!profile) {
        console.warn('[SupabaseAuth] No profile found for auth user:', authUser.id);

        // Retry once on first attempt
        if (retryCount === 0) {
          console.log('[SupabaseAuth] Retrying profile fetch in 2 seconds...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          return fetchUserProfile(authUser, providedSession, retryCount + 1);
        }

        // After retry, use fallback
        console.warn('[SupabaseAuth] No profile found after retry, using fallback');
        return createFallbackUser(authUser);
      }

      console.log('[SupabaseAuth] Profile fetched successfully:', {
        id: profile.id,
        email: profile.email,
        tier: profile.subscription_tier,
      });

      // Map database fields to User interface
      const userProfile: User = {
        id: profile.id,
        email: profile.email || authUser.email || '',
        name: profile.name || '',
        // KAN-name-capture: split-name fields. NULL on legacy rows; the
        // NamePromptGate checks first_name === null to decide whether to
        // fire the post-magic-link form.
        first_name:   profile.first_name   ?? null,
        last_name:    profile.last_name    ?? null,
        display_name: profile.display_name ?? null,
        // KAN-328: username is NOT NULL on new rows after migration. For
        // legacy rows whose backfill hasn't run yet OR rows hit before the
        // migration deployed, fall back to empty string + treat as
        // "not set explicitly" so the prompt fires.
        username: profile.username || '',
        username_set_explicitly: profile.username_set_explicitly === true,
        subscription_tier: profile.subscription_tier?.toLowerCase() || 'free',
        tier_name: profile.subscription_tier || 'FREE',
        is_active: profile.is_active ?? true,
        deleted_at: profile.deleted_at ?? null,
        is_admin: profile.is_admin ?? false,
        email_verified: authUser.email_confirmed_at ? true : false,
        subscription_start_date: profile.subscription_start_date,
        subscription_end_date: profile.subscription_end_date,
        subscription_auto_renew: profile.subscription_auto_renew ?? true,
        predictions_used_this_month: profile.predictions_used_this_month ?? 0,
        has_quick_pick: profile.has_quick_pick ?? true,
        has_full_analysis: profile.has_full_analysis ?? false,
        created_at: profile.created_at,
        updated_at: profile.updated_at,
        last_login: profile.last_login,
        auth_user_id: authUser.id,
        has_used_trial: profile.has_used_trial ?? false,
        trial_started_at: profile.trial_started_at,
        trial_ends_at: profile.trial_ends_at,
        // KAN-3057: tour lifecycle (Phase 2 columns).
        tour_started_at: profile.tour_started_at ?? null,
        tour_completed_at: profile.tour_completed_at ?? null,
        tour_skipped_at: profile.tour_skipped_at ?? null,
        // KAN-26249: user's saved display TZ. Consumed by useDisplayTimezone().
        preferred_timezone: profile.preferred_timezone ?? null,
      };

      return userProfile;
    } catch (error) {
      console.error('[SupabaseAuth] Exception fetching profile:', error);

      // Retry once on exception
      if (retryCount === 0) {
        console.log('[SupabaseAuth] Retrying profile fetch after exception in 2 seconds...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        return fetchUserProfile(authUser, providedSession, retryCount + 1);
      }

      // After retry, use fallback
      console.warn('[SupabaseAuth] Using fallback user after exception');
      return createFallbackUser(authUser);
    }

    // CRITICAL SAFETY NET: This should never be reached, but if it is, return fallback
    // This ensures fetchUserProfile ALWAYS returns a User object, never null/undefined
    console.error('[SupabaseAuth] CRITICAL: Reached end of fetchUserProfile without returning. Creating fallback user.');
    return createFallbackUser(authUser);
  };

  // Initialize auth state from session
  const initializeAuth = async () => {
    try {
      console.log('[SupabaseAuth] Initializing auth...');
      setIsInitializing(true);

      const { data: { session: currentSession }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('[SupabaseAuth] Error getting session:', error);
        setLoading(false);
        setIsInitializing(false);
        return;
      }

      if (currentSession?.user) {
        console.log('[SupabaseAuth] Session found:', currentSession.user.email);
        setSession(currentSession);
        // Do NOT set loading=false here. The profile useEffect fires after setSession()
        // and will clear loading once is_admin is resolved. Setting loading=false here
        // creates a window where loading=false AND user=null, causing ProtectedRoute to
        // redirect before the profile has loaded.
      } else {
        console.log('[SupabaseAuth] No session found');
        setSession(null);
        setLoading(false);
        setIsInitializing(false);
        console.log('[SupabaseAuth] Auth initialization complete (no session)');
      }
    } catch (error) {
      console.error('[SupabaseAuth] Error initializing auth:', error);
      setLoading(false);
      setIsInitializing(false);
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    console.log('[SupabaseAuth] Setting up auth state listener');

    // Initialize
    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      console.log('[SupabaseAuth] Auth state changed:', event, currentSession?.user?.email);
      // ONLY set session - profile fetching happens in separate useEffect
      setSession(currentSession);
    });

    return () => {
      console.log('[SupabaseAuth] Cleaning up auth listener');
      subscription.unsubscribe();
    };
  }, []);

  // Watch session changes and fetch profile.
  // This runs AFTER Supabase has persisted the session, avoiding race conditions.
  // IMPORTANT: this useEffect is responsible for clearing loading=true when a
  // session exists, because initializeAuth() intentionally defers that.
  useEffect(() => {
    if (!session?.user) {
      // No session = logged out (or never logged in)
      if (user) {
        console.log('[SupabaseAuth] Session cleared, logging out user');
        setUser(null);
      }
      // Ensure loading is cleared even if we never had a profile to load
      setLoading(false);
      setIsInitializing(false);
      // KAN-5912: reset so the next signed-in session re-blocks admin routes
      // until the new profile fetch resolves. Without this, profileLoaded
      // stays true across login/logout cycles and a fresh login would short-
      // circuit the wait again.
      setProfileLoaded(false);
      return;
    }

    // Session exists, fetch profile if we don't have user yet or user changed.
    // Optimistic: set fallback user IMMEDIATELY so UI is usable even if the
    // profile fetch is slow (it has been seen to hit the 10s timeout under
    // load — KAN-... A1). The real profile replaces the fallback when it
    // arrives. ProtectedRoute therefore stops bouncing authed users away
    // from /upgrade and /profile while the profile request is in flight.
    if (!user || user.email !== session.user.email) {
      console.log('[SupabaseAuth] Session detected for', session.user.email, '- using fallback, fetching profile in background');

      // Render the app immediately with auth-data fallback.
      const fallback = createFallbackUser(session.user);
      setUser(fallback);
      setLoading(false);
      setIsInitializing(false);
      // KAN-5912: until the real profile lands, is_admin is unknown (the
      // fallback defaults to false). Admin routes must wait for the resolve
      // below before evaluating the requireAdmin guard.
      setProfileLoaded(false);

      // Replace with the real profile once it lands (or keep fallback on failure).
      fetchUserProfile(session.user, session).then((profile) => {
        if (profile) {
          console.log('[SupabaseAuth] Profile loaded, replacing fallback:', profile.email);
          setUser(profile);
          // KAN-46: cache tier so a future profile-fetch failure can fall back to
          // the actual tier instead of silently downgrading to 'free'.
          try {
            localStorage.setItem(TIER_CACHE_KEY, profile.subscription_tier);
            localStorage.setItem(TIER_NAME_CACHE_KEY, profile.tier_name || '');
          } catch { /* ignore */ }
        } else {
          console.warn('[SupabaseAuth] Profile fetch returned null; keeping fallback');
        }
      }).catch((error) => {
        console.error('[SupabaseAuth] Error fetching profile (keeping fallback):', error);
      }).finally(() => {
        // KAN-5912: signal the gate. Done regardless of outcome — the
        // ProtectedRoute should release the loader whether is_admin came
        // back true (admin) or false (real non-admin or fallback after a
        // failed fetch). Without `.finally` a hung fetch would hang admin
        // routes forever.
        setProfileLoaded(true);
      });
    }
  }, [session]);

  // Login with email and password
  const login = async (email: string, password: string) => {
    try {
      console.log('[SupabaseAuth] Logging in:', email);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('[SupabaseAuth] Login error:', error);
        throw new Error(error.message);
      }

      if (!data.user) {
        throw new Error('Login succeeded but no user returned');
      }

      console.log('[SupabaseAuth] Login successful');
      // Profile will be fetched by onAuthStateChange listener
    } catch (error) {
      console.error('[SupabaseAuth] Login exception:', error);
      throw error;
    }
  };

  // Register new user. Returns whether email confirmation is required so the
  // caller (Login.tsx) can show the right next-step UX.
  // KAN-39: previously this function was Promise<void> and the caller assumed
  // a session was always granted on success. With Supabase email-confirm ON
  // (default), signUp returns the user but no session, and the user has to
  // click a verification link before they can log in. The previous code
  // silently redirected to / leaving the user in an anonymous state with
  // no feedback. Now we surface { requiresEmailConfirm } so the form can
  // render a "Check your email" message.
  const register = async (
    email: string,
    password: string,
    name: string,
    username: string,
    clickwrap: {
      tos_accepted_at: string;
      tos_version: string;
      marketing_consent?: boolean;
      marketing_consent_at?: string | null;
    }
  ) => {
    // KAN-26088: emit a Sentry breadcrumb on every register() entry so the
    // signup funnel is reconstructable from a crash report alone. Tagged
    // with `signup.flow` so a future inbound-filter rule can pivot on it.
    addBreadcrumb('signup.flow: register() entry', {
      tag: 'signup.flow',
      email_domain: email.split('@')[1] || 'unknown',
    });

    try {
      console.log('[SupabaseAuth] Registering new user:', email);

      // KAN-124: clickwrap fields ride along in raw_user_meta_data so the
      // public.handle_new_user trigger can stamp them onto the public.users
      // row in the same transaction as the auth.users insert. The frontend
      // already gates submission on the checkbox; the trigger trusts these.
      // KAN-328: same goes for `username` — handle_new_user reads it and
      // sets username_set_explicitly=true so the legacy prompt doesn't fire.
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            username,
            tos_accepted_at: clickwrap.tos_accepted_at,
            tos_version: clickwrap.tos_version,
            // KAN-152: marketing consent rides along in raw_user_meta_data.
            // CASL/GDPR-friendly default is opt-in: only stamped when the
            // user actively ticked the optional checkbox. `false` means
            // the user saw the box and declined; `null` means the user
            // signed up before the box existed (legacy rows).
            marketing_consent: clickwrap.marketing_consent ?? false,
            marketing_consent_at: clickwrap.marketing_consent_at ?? null,
          },
          emailRedirectTo: `${getWebOrigin()}/login`,
        },
      });

      // KAN-26088: explicit branch-by-branch interpretation of the supabase-js
      // signUp() return shape. The previous code threw "Could not create
      // account" whenever `data.user` was falsy — which is wrong for the
      // happy email-confirm path on supabase-js@2.106.0.
      //
      // Root cause: the auth-js v2.106 `_sessionResponse` xform
      // (`node_modules/@supabase/auth-js/src/lib/fetch.ts:258`) reads the user
      // off `data.user`, but the GoTrue `/signup` endpoint returns the User
      // shape at the TOP level of the body when email-confirm is ON (no
      // `data.user` wrapper, no access_token). So a successful "check your
      // email" signup arrives here as `{ user: null, session: null, error: null }`
      // — indistinguishable from a silent failure with the old logic.
      // `_userResponse` has the `?? (data as User)` fallback for the same
      // server shape; `_sessionResponse` does not. supabase/auth-js bug
      // mirror — but we cannot wait on an upstream patch, so we branch on
      // `error` here instead.
      //
      // Three valid outcomes:
      //   1. error present                        → real failure (rethrow)
      //   2. !error && data.session && data.user  → fully signed in
      //   3. !error && (no session)               → email confirmation sent
      //
      // The auth-state-change listener will pick up case 2 and fetch the
      // profile; case 3 hands control back to Login.tsx which renders the
      // check-your-email panel via { requiresEmailConfirm: true }.

      if (error) {
        console.error('[SupabaseAuth] Registration error:', error);
        addBreadcrumb('signup.flow: register() error branch', {
          tag: 'signup.flow',
          error_message: error.message || '<empty>',
          error_status: (error as any).status,
        });
        // Coerce empty/object messages to something the user can act on.
        const msg = (error.message && error.message.trim() && error.message !== '{}')
          ? error.message
          : 'Could not create account. Please try again or contact support.';
        throw new Error(msg);
      }

      // Three-way classification:
      //   - isSignedIn:   user + session present → fully authed
      //   - !data.session: email-confirm path → user will arrive after click
      const isSignedIn = !!(data.user && data.session);

      addBreadcrumb('signup.flow: register() success branch', {
        tag: 'signup.flow',
        branch: isSignedIn ? 'success-signedin' : 'success-confirm',
        has_user: !!data.user,
        has_session: !!data.session,
      });

      console.log('[SupabaseAuth] Registration successful', {
        userId: data.user?.id,
        hasSession: !!data.session,
        branch: isSignedIn ? 'success-signedin' : 'success-confirm',
      });

      // KAN-65: top-of-funnel signup event. Fires regardless of whether
      // email-confirm is on (no session yet) — the user completed the signup
      // form and Supabase accepted the credentials. Distinct ID is the
      // brand-new auth user id when we have it; on the bare-User email-confirm
      // path data.user can be null even though the auth row was created, so
      // fall back to 'pending_email_confirm' for the few users on that path.
      captureEvent('signup_completed', {
        auth_user_id: data.user?.id ?? 'pending_email_confirm',
        requires_email_confirm: !data.session,
        // No PII — name/email are deliberately omitted.
      });

      // KAN-48 / KAN-42: write the name through `upsert`, not `update`. The
      // auth-trigger that mirrors auth.users → public.users may not have fired
      // yet by the time this runs; an `update` would match zero rows and
      // silently drop the name, leaving the Profile page with an empty name
      // field. Only attempt when a session was granted (email-confirm OFF) —
      // with email-confirm ON the user has no RLS permission to write
      // public.users until they verify, and the auth-trigger reads name from
      // raw_user_meta_data instead.
      // KAN-26088: also gate on `data.user` being non-null. On the bare-User
      // email-confirm path supabase-js@2.106.0 returns user=null even when
      // the auth row was created — we have no auth_user_id to upsert against,
      // and skipping is safe because handle_new_user has already populated
      // public.users from raw_user_meta_data in the same transaction.
      if (data.session && data.user) {
        try {
          await supabase
            .from('users')
            .upsert(
              {
                auth_user_id: data.user.id,
                name,
                email: data.user.email || email,
                // KAN-328: same upsert-belt-and-braces logic as `name` —
                // the trigger should populate username from raw_user_meta_data,
                // but if a race causes upsert to be the first writer we
                // want the user-chosen handle stuck on the row, not whatever
                // the trigger fallback would have picked.
                username,
                username_set_explicitly: true,
                // KAN-124: also upsert clickwrap fields. The trigger stamps
                // them on first insert from raw_user_meta_data, but if the
                // upsert here is what creates the row (race / older flows),
                // we still want the audit trail populated.
                tos_accepted_at: clickwrap.tos_accepted_at,
                tos_version: clickwrap.tos_version,
              },
              { onConflict: 'auth_user_id' }
            );
        } catch (e) {
          console.warn('[SupabaseAuth] Profile name upsert failed (non-fatal):', e);
        }
      }

      return { requiresEmailConfirm: !data.session };
    } catch (error) {
      console.error('[SupabaseAuth] Registration exception:', error);
      // KAN-26088: emit the failure breadcrumb here so any thrown path (from
      // the explicit `throw new Error(msg)` above, or from supabase-js itself)
      // leaves a trail before bubbling out to Login.tsx.
      addBreadcrumb('signup.flow: register() exception', {
        tag: 'signup.flow',
        branch: 'error',
        message: (error as Error)?.message || '<unknown>',
      });
      throw error;
    }
  };

  // Logout — defensive (KAN-50).
  //
  // The previous implementation awaited `supabase.auth.signOut()` BEFORE
  // clearing local state. When the Supabase auth API was sluggish (or hung),
  // the Promise never resolved → no error fired, no success path ran, the
  // user appeared signed in even after clicking Sign Out. P1 production bug.
  //
  // Fix: clear local state synchronously FIRST (the user-visible part), then
  // best-effort revoke the server token with a short timeout. A flaky network
  // can no longer keep someone logged in client-side.
  const logout = async () => {
    console.log('[SupabaseAuth] Logging out');

    // 1) Clear local state immediately. This is what the user sees.
    setUser(null);
    setSession(null);
    // KAN-5912: reset profileLoaded so the next login re-arms the
    // admin-route loader gate.
    setProfileLoaded(false);

    // 2) Clear cached tier so the next fresh login can't show a stale chip.
    //    Also nuke the Supabase auth token directly — when supabase.auth.signOut()
    //    times out (KAN-50 follow-up: re-QA caught this), the SDK never gets to
    //    remove its token from localStorage, and a page refresh would silently
    //    re-authenticate. Clearing here means logout sticks across reloads
    //    regardless of whether the network call resolves.
    try {
      localStorage.removeItem(TIER_CACHE_KEY);
      localStorage.removeItem(TIER_NAME_CACHE_KEY);
      // Supabase SDK keys: project-prefixed `sb-<project-ref>-auth-token` plus
      // any older variants. Sweep anything matching the prefix.
      Object.keys(localStorage)
        .filter((k) => k.startsWith('sb-') && k.endsWith('-auth-token'))
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }

    // 3) Best-effort server revoke with a 3s ceiling so a hung network
    //    doesn't keep the spinner up forever.
    try {
      await Promise.race([
        supabase.auth.signOut({ scope: 'local' }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('signOut timeout')), 3000)
        ),
      ]);
      console.log('[SupabaseAuth] Logout successful');
    } catch (err: any) {
      // We've already cleared local state — this is informational only.
      if (err?.message?.includes('Auth session missing')) {
        console.log('[SupabaseAuth] Server session already missing — local state cleared');
      } else {
        console.warn('[SupabaseAuth] Server signOut failed (local state cleared):', err?.message);
      }
    }

    // 4) KAN-25485 — defensive IndexedDB nuke. `supabase.auth.signOut()`
    //    only clears the storage adapter the client was constructed with
    //    (localStorage on web). Supabase JS v2 can ALSO fall back to
    //    `localforage` / `keyval-store` IndexedDB databases when
    //    localStorage is quota-exhausted or unavailable, and `localStorage`
    //    `.removeItem` calls above don't touch those. Fresh-user E2E
    //    walkthroughs (KAN-25485) caught `sb-*-auth-token` rehydrating from
    //    IDB on the next page load and the auth context picking up the
    //    stale session — UsernamePromptGate then fired against the prior
    //    test user. The sweep is idempotent and a no-op when IDB is empty.
    try {
      await clearWebAuthStorage();
    } catch {
      // Storage-clear failure is non-fatal — local state already cleared.
    }
  };

  // Update profile
  const updateProfile = async (data: {
    name?: string;
    email?: string;
    current_password?: string;
    new_password?: string;
  }) => {
    try {
      console.log('[SupabaseAuth] Updating profile');

      if (!user) {
        throw new Error('No user logged in');
      }

      // Update password if provided
      if (data.new_password && data.current_password) {
        // First verify current password by attempting to sign in
        const { error: verifyError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: data.current_password,
        });

        if (verifyError) {
          throw new Error('Current password is incorrect');
        }

        // Update password
        const { error: passwordError } = await supabase.auth.updateUser({
          password: data.new_password,
        });

        if (passwordError) {
          throw new Error(passwordError.message);
        }
      }

      // Update email if provided
      if (data.email && data.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: data.email,
        });

        if (emailError) {
          throw new Error(emailError.message);
        }
      }

      // Update name in public.users table
      if (data.name) {
        const { error: nameError } = await supabase
          .from('users')
          .update({ name: data.name, updated_at: new Date().toISOString() })
          .eq('auth_user_id', user.auth_user_id);

        if (nameError) {
          throw new Error(nameError.message);
        }
      }

      // Refresh user profile
      await refreshUser();

      console.log('[SupabaseAuth] Profile updated successfully');
    } catch (error) {
      console.error('[SupabaseAuth] Update profile exception:', error);
      throw error;
    }
  };

  // Upgrade subscription via Stripe
  const upgradeSubscription = async (tier: string, promoCode?: string) => {
    console.log('[SupabaseAuth] Upgrade subscription called for tier:', tier, promoCode ? `(promo: ${promoCode})` : '');

    if (!user) {
      throw new Error('User not authenticated');
    }

    // KAN-65: funnel checkpoint — user clicked an upgrade CTA and we're
    // about to redirect to Stripe. `upgrade_completed` fires on the
    // PaymentSuccess page after Stripe redirects back, so the gap between
    // these two events is the Stripe-side conversion rate.
    captureEvent('upgrade_cta_clicked', {
      target_tier: tier.toLowerCase(),
      from_tier: user.subscription_tier,
      has_promo: !!promoCode,
    });

    // KAN-41: previously, the first trial-button click frequently produced
    // "Failed to process subscription change" while the backend actually
    // returned 200 + valid Stripe URL. Two contributing factors:
    //   1. Token rotation race: the Supabase access_token sent on the first
    //      call was sometimes a freshly-rotated token the backend hadn't seen
    //      yet (the listener that refreshes session can fire concurrently
    //      with the click handler). Subsequent click reuses the now-stable
    //      token and succeeds.
    //   2. Generic catch swallowed the actual axios error, leaving users
    //      with a vague banner.
    // Fix: refresh the session before the request, log the actual error
    // structure on failure, and retry once on a network/transient error.
    const callUpgrade = async () => {
      // Force-refresh to ensure backend sees the same access_token we send.
      try { await supabase.auth.refreshSession(); } catch { /* non-fatal */ }
      // KAN-76: forward optional promo so backend can pre-apply the discount
      // at Stripe Checkout. Backend silently falls back to allow_promotion_codes
      // if the code is unknown/invalid, so this is safe to send unconditionally.
      // KAN-22544 — Stripe must redirect to the post-purchase welcome
      // surface, not the upgrade card. Prior value (`/upgrade`) dropped
      // paying users back on the pricing card they just clicked through
      // (no welcome / no receipt / no unlock list). Cancel still routes
      // to `/upgrade` so retries land on the same pricing surface.
      const payload: Record<string, string> = {
        tier: tier.toLowerCase(),
        success_url: `${getWebOrigin()}/payment-success`,
        cancel_url: `${getWebOrigin()}/upgrade`,
      };
      if (promoCode) payload.promo_code = promoCode;
      return unifiedApi.post('/api/payments/upgrade', payload);
    };

    try {
      // On native, direct users to the website for payments
      if (isNativePlatform()) {
        throw new Error('Please visit aightbetsports.com to manage your subscription.');
      }

      let response: any;
      try {
        response = await callUpgrade();
      } catch (firstErr: any) {
        const status = firstErr?.response?.status;
        // Retry once on transient failures (401/403 token race, 5xx, network)
        if (!status || status === 401 || status === 403 || status >= 500) {
          console.warn('[SupabaseAuth] Upgrade first attempt failed, retrying once:', { status, message: firstErr?.message });
          await new Promise((r) => setTimeout(r, 500));
          response = await callUpgrade();
        } else {
          throw firstErr;
        }
      }

      // Redirect to Stripe checkout or portal
      if (response?.redirect_url) {
        window.location.href = response.redirect_url;
      } else {
        console.error('[SupabaseAuth] Upgrade response missing redirect_url:', response);
        throw new Error('Could not start checkout. Please try again or contact support.');
      }
    } catch (error: any) {
      console.error('[SupabaseAuth] Upgrade subscription error:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      const detail = error?.response?.data?.detail;
      const msg = (detail && String(detail).trim())
        || (error?.message && String(error.message).trim() && error.message !== '{}')
        || 'Could not start checkout. Please try again or contact support.';
      throw new Error(msg);
    }
  };

  // KAN-328: live username availability check.
  //
  // Calls the public `username_available` RPC. Going through the supabase-js
  // client's `.rpc()` method hangs indefinitely on this project (verified in
  // post-deploy UAT 2026-05-04 — the promise never resolved, no fetch ever
  // issued). Workaround is the same pattern used elsewhere in the codebase
  // for stuck SDK paths: raw `fetch` to PostgREST with explicit
  // `Content-Profile: public` (without it PostgREST defaults to
  // `graphql_public` and 404s).
  //
  // useCallback memoization is still required: consumers (Login.tsx,
  // UsernamePromptModal) put this in a useEffect dep array for the 400ms
  // debounce, and a fresh function identity on every provider re-render
  // would cancel the timer before it fires. Empty deps is safe — the URL
  // and key come from import.meta.env at module load.
  const checkUsernameAvailability = useCallback(
    async (username: string): Promise<UsernameAvailability> => {
      try {
        // Source the URL/key from `config` (which falls back through
        // VITE_SUPABASE_PUBLISHABLE_KEY → VITE_SUPABASE_ANON_KEY) instead of
        // reading VITE_SUPABASE_ANON_KEY directly. A truncated env-var value
        // in Vercel for VITE_SUPABASE_ANON_KEY produced a 116-char key here
        // and 401'd every signup-time availability check; the supabase-js
        // client survived because it goes through the same fallback chain.
        const url = config.supabase.url;
        const key = config.supabase.anonKey;
        const res = await fetch(`${url}/rest/v1/rpc/username_available`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Content-Profile': 'public',
            apikey: key,
            Authorization: `Bearer ${key}`,
          },
          body: JSON.stringify({ p_username: username }),
        });
        if (!res.ok) {
          console.warn('[SupabaseAuth] username_available HTTP', res.status);
          return { available: false };
        }
        return (await res.json()) as UsernameAvailability;
      } catch (err) {
        console.warn('[SupabaseAuth] username_available exception:', err);
        return { available: false };
      }
    },
    [],
  );

  // KAN-328: authenticated set/update. Same raw-fetch workaround as
  // checkUsernameAvailability. Includes the user's access token so the RPC
  // can resolve `auth.uid()` and find the calling user's row.
  const setUsername = useCallback(
    async (username: string): Promise<SetUsernameResult> => {
      try {
        const url = config.supabase.url;
        const key = config.supabase.anonKey;
        // Pull the current access token directly from the supabase auth
        // store. `session?.access_token` from the closure can be stale
        // immediately after login because the closure captured the previous
        // session value.
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        const accessToken = currentSession?.access_token || key;
        const res = await fetch(`${url}/rest/v1/rpc/set_username`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Content-Profile': 'public',
            apikey: key,
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ p_username: username }),
        });
        if (!res.ok) {
          console.warn('[SupabaseAuth] set_username HTTP', res.status);
          return { ok: false, reason: 'taken' };
        }
        const result = (await res.json()) as SetUsernameResult;
        if (result.ok) {
          // Refresh so user.username + user.username_set_explicitly land in
          // every consumer (UsernamePromptGate dismisses, Profile updates).
          await refreshUser();
        }
        return result;
      } catch (err) {
        console.warn('[SupabaseAuth] set_username exception:', err);
        return { ok: false };
      }
    },
    [],
  );

  // KAN-name-capture: post-magic-link first/last/display update. Mirrors
  // setUsername's raw-fetch + access-token pattern (the supabase-js client
  // closure-stale-session bug is documented at the setUsername call site).
  const setNameDetails = useCallback(
    async (args: {
      first_name: string;
      last_name?: string;
      display_name?: string;
    }): Promise<{ ok: boolean; reason?: string }> => {
      try {
        const url = config.supabase.url;
        const key = config.supabase.anonKey;
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        const accessToken = currentSession?.access_token || key;
        const res = await fetch(`${url}/rest/v1/rpc/set_name_details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Content-Profile': 'public',
            apikey: key,
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            p_first_name:   args.first_name,
            p_last_name:    args.last_name   ?? '',
            p_display_name: args.display_name ?? '',
          }),
        });
        if (!res.ok) {
          console.warn('[SupabaseAuth] set_name_details HTTP', res.status);
          return { ok: false, reason: 'http_error' };
        }
        const result = (await res.json()) as { ok: boolean; reason?: string };
        if (result?.ok) {
          // Refresh so first_name/last_name/display_name land in every
          // consumer (NamePromptGate dismisses, Profile updates).
          await refreshUser();
        }
        return result ?? { ok: false };
      } catch (err) {
        console.warn('[SupabaseAuth] set_name_details exception:', err);
        return { ok: false, reason: 'exception' };
      }
    },
    [],
  );

  // Refresh user profile from database
  const refreshUser = async () => {
    try {
      console.log('[SupabaseAuth] Refreshing user profile');

      const { data: { session: currentSession }, error } = await supabase.auth.getSession();

      if (error || !currentSession?.user) {
        console.error('[SupabaseAuth] No session found during refresh');
        setUser(null);
        return;
      }

      const profile = await fetchUserProfile(currentSession.user, currentSession);
      setUser(profile);

      console.log('[SupabaseAuth] User profile refreshed');
    } catch (error) {
      console.error('[SupabaseAuth] Refresh user exception:', error);
    }
  };

  // Legacy compatibility methods (no-op)
  const getToken = () => {
    return session?.access_token || null;
  };

  const setToken = (token: string) => {
    console.warn('[SupabaseAuth] setToken called but is a no-op with Supabase Auth');
  };

  const clearToken = () => {
    console.warn('[SupabaseAuth] clearToken called but is a no-op with Supabase Auth');
  };

  const isTokenExpired = () => {
    if (!session) return true;
    const expiresAt = session.expires_at;
    if (!expiresAt) return false;
    return expiresAt * 1000 < Date.now();
  };

  const clearCorruptedAuth = () => {
    console.log('[SupabaseAuth] clearCorruptedAuth called, signing out');
    logout();
  };

  // Initialize unifiedApi with Supabase token provider for authenticated requests
  useEffect(() => {
    unifiedApi.setTokenProvider({
      getToken: () => session?.access_token || null,
      clearToken: () => {
        // Token management is handled by Supabase auth
      },
      isTokenExpired: () => {
        if (!session) return true;
        const expiresAt = session.expires_at;
        if (!expiresAt) return false;
        return expiresAt * 1000 < Date.now();
      },
    });

    console.log('[SupabaseAuth] Initialized unifiedApi token provider');
  }, [session]);

  const value = {
    user,
    hasSession: !!session,
    profileLoaded,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin ?? false,
    login,
    register,
    logout,
    loading,
    isInitializing,
    session,
    updateProfile,
    upgradeSubscription,
    refreshUser,
    checkUsernameAvailability,
    setUsername,
    setNameDetails,
    getToken,
    setToken,
    clearToken,
    isTokenExpired,
    clearCorruptedAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Export for backward compatibility
export default SupabaseAuthProvider;
